import { Category } from "@/types/transaction.type";
import React, { createContext, useContext, useState, ReactNode } from "react";

type CategoryModalState = {
  open: boolean;
  mode: "create" | "edit" | "delete" | "limit";
  category?: Category;
  onCreateSuccess?: (category: Category) => void;
};

type CategoryModalContextType = {
  showCreate: (onSuccess?: (category: Category) => void) => void;
  showEdit: (category: Category) => void;
  showDelete: (category: Category) => void;
  /** Set, change, or remove this category's monthly spending limit. */
  showLimit: (category: Category) => void;
  hide: () => void;
  state: CategoryModalState;
};

const CategoryModalContext = createContext<
  CategoryModalContextType | undefined
>(undefined);

export const useCategoryModal = () => {
  const ctx = useContext(CategoryModalContext);
  if (!ctx)
    throw new Error(
      "useCategoryModal must be used within CategoryModalProvider"
    );
  return ctx;
};

export const CategoryModalProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [state, setState] = useState<CategoryModalState>({
    open: false,
    mode: "create",
  });

  const showCreate = (onSuccess?: (category: Category) => void) =>
    setState({ open: true, mode: "create", onCreateSuccess: onSuccess });
  const showEdit = (category: Category) =>
    setState({ open: true, mode: "edit", category });
  const showDelete = (category: Category) =>
    setState({ open: true, mode: "delete", category });
  const showLimit = (category: Category) =>
    setState({ open: true, mode: "limit", category });
  const hide = () =>
    setState({
      open: false,
      mode: "create",
      category: undefined,
      onCreateSuccess: undefined,
    });

  return (
    <CategoryModalContext.Provider
      value={{ showCreate, showEdit, showDelete, showLimit, hide, state }}
    >
      {children}
    </CategoryModalContext.Provider>
  );
};
